import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle, X, ChevronRight, ChevronLeft, Volume2, VolumeX, Search, BookOpen, Video, Lightbulb, CheckCircle, Clock, Star, Sparkles, ArrowRight, MessageCircle, Shield, Settings, User, Folder, Briefcase, BarChart2, FileText, Zap, Home, Award, SkipForward, RefreshCw, ExternalLink, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { cn } from '../../lib/utils';
import { TUTORIALS, getTutorialsBySpace, getAllTutorials } from '../../data/tutorials';

// === ICONS MAPPING ===
const _ICON_MAP = {
  'dashboard': Home,
  'user': User,
  'folder': Folder,
  'settings': Settings,
  'shield': Shield,
  'message': MessageCircle,
  'briefcase': Briefcase,
  'chart': BarChart2,
  'file': FileText,
  'zap': Zap,
  'award': Award,
  'video': Video,
  'book': BookOpen,
  'lightbulb': Lightbulb,
  'star': Star
};

// === TEXT-TO-SPEECH HOOK ===
const useSpeechSynthesis = () => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isSupported] = useState(() => typeof window !== 'undefined' && 'speechSynthesis' in window);
  const utteranceRef = useRef(null);

  const speak = useCallback((text) => {
    if (!isSupported || !text) return;
    
    // Annuler la lecture en cours
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'fr-FR';
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.volume = 1;
    
    // Trouver une voix française
    const voices = window.speechSynthesis.getVoices();
    const frenchVoice = voices.find(v => v.lang.includes('fr')) || voices[0];
    if (frenchVoice) utterance.voice = frenchVoice;
    
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    
    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  }, [isSupported]);

  const stop = useCallback(() => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  }, []);

  const toggle = useCallback((text) => {
    if (isSpeaking) {
      stop();
    } else {
      speak(text);
    }
  }, [isSpeaking, speak, stop]);

  return { speak, stop, toggle, isSpeaking, isSupported };
};

// === ANIMATED BACKGROUND ===
export const AnimatedBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {/* Gradient Orbs */}
    <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-radial from-violet-500/20 via-transparent to-transparent animate-pulse" 
         style={{ animationDuration: '4s' }} />
    <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-radial from-cyan-500/20 via-transparent to-transparent animate-pulse" 
         style={{ animationDuration: '5s', animationDelay: '1s' }} />
    <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 bg-gradient-radial from-fuchsia-500/10 via-transparent to-transparent animate-pulse" 
         style={{ animationDuration: '6s', animationDelay: '2s' }} />
    
    {/* Grid Pattern */}
    <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />
    
    {/* Floating Particles - positions fixes pour éviter Math.random pendant le render */}
    {[
      { left: '10%', top: '20%', duration: '4s', delay: '0s' },
      { left: '25%', top: '60%', duration: '5s', delay: '0.5s' },
      { left: '40%', top: '30%', duration: '6s', delay: '1s' },
      { left: '55%', top: '80%', duration: '4.5s', delay: '1.5s' },
      { left: '70%', top: '15%', duration: '5.5s', delay: '0.2s' },
      { left: '85%', top: '45%', duration: '4s', delay: '0.8s' },
      { left: '15%', top: '75%', duration: '6.5s', delay: '1.2s' },
      { left: '60%', top: '55%', duration: '5s', delay: '0.3s' },
      { left: '30%', top: '90%', duration: '4.8s', delay: '0.7s' },
      { left: '80%', top: '70%', duration: '5.2s', delay: '1.8s' }
    ].map((particle, i) => (
      <div
        key={i}
        className="absolute w-1 h-1 bg-white/30 rounded-full animate-float"
        style={{
          left: particle.left,
          top: particle.top,
          animationDuration: particle.duration,
          animationDelay: particle.delay
        }}
      />
    ))}
  </div>
);

// === TUTORIAL STEP CARD ===
const TutorialStepCard = ({ step, index, isActive, onClick }) => {
  return (
    <div
      onClick={onClick}
      className={cn(
        "p-4 rounded-xl cursor-pointer transition-all duration-300",
        "border border-white/10 backdrop-blur-sm",
        isActive 
          ? "bg-gradient-to-r from-violet-500/30 to-cyan-500/30 border-violet-400/50 shadow-lg shadow-violet-500/20" 
          : "bg-white/5 hover:bg-white/10 hover:border-white/20"
      )}
    >
      <div className="flex items-start gap-3">
        <div className={cn(
          "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold",
          isActive 
            ? "bg-gradient-to-br from-violet-500 to-cyan-500 text-white" 
            : "bg-white/10 text-white/60"
        )}>
          {index + 1}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className={cn(
            "font-semibold truncate",
            isActive ? "text-white" : "text-white/80"
          )}>
            {step.title}
          </h4>
          <p className="text-sm text-white/50 line-clamp-2 mt-1">
            {step.content}
          </p>
        </div>
      </div>
    </div>
  );
};

// === TUTORIAL PLAYER ===
export const TutorialPlayer = ({ tutorial, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [_isPlaying, _setIsPlaying] = useState(true);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { speak, stop, toggle: _toggle, isSpeaking, isSupported } = useSpeechSynthesis();
  
  const step = tutorial.steps[currentStep];
  const progress = ((currentStep + 1) / tutorial.steps.length) * 100;
  const isLastStep = currentStep === tutorial.steps.length - 1;
  const isFirstStep = currentStep === 0;

  // Auto-read when step changes
  useEffect(() => {
    if (voiceEnabled && step) {
      const textToRead = `${step.title}. ${step.content}`;
      speak(textToRead);
    }
    return () => stop();
  }, [currentStep, voiceEnabled, step, speak, stop]);

  const handleNext = () => {
    if (isLastStep) {
      onComplete?.();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const toggleVoice = () => {
    if (voiceEnabled) {
      stop();
    } else if (step) {
      speak(`${step.title}. ${step.content}`);
    }
    setVoiceEnabled(!voiceEnabled);
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
      />
      
      {/* Player Container */}
      <div className={cn(
        "relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl shadow-2xl",
        "border border-white/10 overflow-hidden transition-all duration-300",
        isFullscreen 
          ? "w-full h-full m-4 max-w-none" 
          : "w-full max-w-4xl mx-4"
      )}>
        <AnimatedBackground />
        
        {/* Header */}
        <div className="relative z-10 p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{tutorial.name}</h2>
                <p className="text-sm text-white/50">
                  Étape {currentStep + 1} sur {tutorial.steps.length}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Voice Toggle */}
              {isSupported && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleVoice}
                  className={cn(
                    "text-white/60 hover:text-white hover:bg-white/10",
                    voiceEnabled && "text-violet-400"
                  )}
                >
                  {voiceEnabled ? (
                    <Volume2 className="w-5 h-5" />
                  ) : (
                    <VolumeX className="w-5 h-5" />
                  )}
                </Button>
              )}
              
              {/* Fullscreen Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="text-white/60 hover:text-white hover:bg-white/10"
              >
                {isFullscreen ? (
                  <Minimize2 className="w-5 h-5" />
                ) : (
                  <Maximize2 className="w-5 h-5" />
                )}
              </Button>
              
              {/* Close */}
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="text-white/60 hover:text-white hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-cyan-500 transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Step Content */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center text-white font-bold">
                    {currentStep + 1}
                  </div>
                  <h3 className="text-xl font-bold text-white">{step.title}</h3>
                  
                  {/* Voice indicator */}
                  {isSpeaking && (
                    <div className="flex items-center gap-2 ml-auto">
                      <div className="flex gap-1">
                        {[12, 18, 14].map((height, i) => (
                          <div
                            key={i}
                            className="w-1 bg-violet-400 rounded-full animate-pulse"
                            style={{
                              height: `${height}px`,
                              animationDelay: `${i * 0.1}s`
                            }}
                          />
                        ))}
                      </div>
                      <span className="text-xs text-violet-400">En lecture...</span>
                    </div>
                  )}
                </div>
                
                <p className="text-white/80 leading-relaxed text-lg">
                  {step.content}
                </p>
                
                {/* Position indicator */}
                {step.position && (
                  <div className="mt-4 flex items-center gap-2">
                    <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30">
                      <Lightbulb className="w-3 h-3 mr-1" />
                      Position: {step.position}
                    </Badge>
                  </div>
                )}
              </div>
              
              {/* Visual Demo Area */}
              <div className="bg-gradient-to-br from-violet-500/10 to-cyan-500/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10 text-center">
                <div className="w-24 h-24 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center animate-bounce" style={{ animationDuration: '2s' }}>
                  <Sparkles className="w-12 h-12 text-white" />
                </div>
                <p className="text-white/60 text-sm">
                  💡 Suivez les indicateurs visuels pour localiser l&apos;élément
                </p>
              </div>
            </div>
            
            {/* Steps Sidebar */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-white/60 uppercase tracking-wider">
                Toutes les étapes
              </h4>
              <ScrollArea className="h-[300px] pr-4">
                <div className="space-y-2">
                  {tutorial.steps.map((s, i) => (
                    <TutorialStepCard
                      key={i}
                      step={s}
                      index={i}
                      isActive={i === currentStep}
                      onClick={() => setCurrentStep(i)}
                    />
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>
        </div>
        
        {/* Footer Navigation */}
        <div className="relative z-10 p-6 border-t border-white/10 bg-black/20">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={handlePrev}
              disabled={isFirstStep}
              className="text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-30"
            >
              <ChevronLeft className="w-5 h-5 mr-2" />
              Précédent
            </Button>
            
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={onClose}
                className="text-white/40 hover:text-white/60"
              >
                <SkipForward className="w-4 h-4 mr-2" />
                Passer
              </Button>
            </div>
            
            <Button
              onClick={handleNext}
              className="bg-gradient-to-r from-violet-500 to-cyan-500 text-white hover:from-violet-600 hover:to-cyan-600"
            >
              {isLastStep ? (
                <>
                  Terminer
                  <CheckCircle className="w-5 h-5 ml-2" />
                </>
              ) : (
                <>
                  Suivant
                  <ChevronRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// === TUTORIAL CARD ===
export const TutorialCard = ({ tutorial, onStart, completed }) => {
  const stepsCount = tutorial.steps?.length || 0;
  const estimatedTime = Math.ceil(stepsCount * 0.5);
  
  return (
    <div className={cn(
      "group relative p-5 rounded-2xl transition-all duration-300 cursor-pointer",
      "bg-white/5 backdrop-blur-sm border border-white/10",
      "hover:bg-white/10 hover:border-violet-500/30 hover:shadow-lg hover:shadow-violet-500/10",
      completed && "border-green-500/30"
    )}
    onClick={onStart}
    >
      {/* Completed badge */}
      {completed && (
        <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
          <CheckCircle className="w-4 h-4 text-white" />
        </div>
      )}
      
      <div className="flex items-start gap-4">
        <div className={cn(
          "w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0",
          "bg-gradient-to-br from-violet-500/20 to-cyan-500/20 group-hover:from-violet-500/30 group-hover:to-cyan-500/30"
        )}>
          <BookOpen className="w-6 h-6 text-violet-400" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-white group-hover:text-violet-300 transition-colors">
            {tutorial.name}
          </h4>
          
          <div className="flex items-center gap-3 mt-2">
            <span className="flex items-center gap-1 text-xs text-white/50">
              <Clock className="w-3 h-3" />
              ~{estimatedTime} min
            </span>
            <span className="flex items-center gap-1 text-xs text-white/50">
              <FileText className="w-3 h-3" />
              {stepsCount} étapes
            </span>
          </div>
        </div>
        
        <ArrowRight className="w-5 h-5 text-white/30 group-hover:text-violet-400 group-hover:translate-x-1 transition-all" />
      </div>
    </div>
  );
};
